/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

/**
 * Dynamic swatch image updates.
 *
 * When a user changes an option (e.g. Model), color swatches need to update
 * their background images to reflect the correct variant's metafield image.
 * Liquid renders the initial state; this JS handles subsequent option changes.
 *
 * Handles three swatch structures:
 * 1. PDP color-swatch-list grids (.color-swatch with radio + label)
 * 2. Product card swatches (g-product-card inner div with data-swatch-value)
 * 3. Sticky form / dropdown dots (.combo-box__color-swatch, .select__color-swatch)
 */
(function () {
  var SEPARATOR = '|||';

  function getSelectedOptions(scope) {
    var selected = {};
    for (var i = 1; i <= 3; i++) {
      var radio = scope.querySelector('input[name="option' + i + '"]:checked');
      if (radio) { selected[i] = radio.value; continue; }
      var select = scope.querySelector('select[name="option' + i + '"]');
      if (select) { selected[i] = select.value; }
    }
    return selected;
  }

  function buildComboKey(swatchValue, optionPosition, selectedValues, optionCount) {
    var parts = [];
    for (var i = 1; i <= optionCount; i++) {
      parts.push(i === optionPosition ? swatchValue : (selectedValues[i] || ''));
    }
    return parts.join(SEPARATOR);
  }

  function applySwatchStyle(el, imageUrl, fallbackStyle) {
    if (imageUrl) {
      el.style.cssText = 'background-image: url(' + imageUrl + ')';
    } else {
      var fallback = fallbackStyle || el.dataset.swatchDefaultStyle;
      if (fallback) { el.style.cssText = fallback; }
    }
  }

  /**
   * Parse the JSON image map from a swatch container.
   */
  function getImageMap(container) {
    var mapScript = container.querySelector('script[data-swatch-image-map]');
    if (!mapScript) return null;
    try {
      var map = JSON.parse(mapScript.textContent);
      return Object.keys(map).length ? map : null;
    } catch (e) { return null; }
  }

  /**
   * Update all swatch images within a [data-swatch-option-position] container.
   */
  function updateSwatchList(list, selectedValues, optionCount) {
    var imageMap = getImageMap(list);
    if (!imageMap) return;

    var optionPosition = parseInt(list.dataset.swatchOptionPosition, 10);

    // Strategy 1: PDP/card color-swatch grids (radio + label inside .color-swatch)
    list.querySelectorAll('.color-swatch').forEach(function (swatch) {
      var radio = swatch.querySelector('input[type="radio"]');
      var label = swatch.querySelector('label');
      if (!radio || !label) return;
      var key = buildComboKey(radio.value, optionPosition, selectedValues, optionCount);
      applySwatchStyle(label, imageMap[key]);
    });

    // Strategy 2: g-product-card swatches (inner div with data-swatch-value)
    list.querySelectorAll('[data-swatch-value][data-swatch-default-style]').forEach(function (el) {
      var key = buildComboKey(el.dataset.swatchValue, optionPosition, selectedValues, optionCount);
      applySwatchStyle(el, imageMap[key]);
    });

    // Strategy 3: Dropdown combo-box color dots (have data-swatch-value)
    list.querySelectorAll('.combo-box__color-swatch[data-swatch-value]').forEach(function (el) {
      var key = buildComboKey(el.dataset.swatchValue, optionPosition, selectedValues, optionCount);
      applySwatchStyle(el, imageMap[key]);
    });

    // Strategy 4: Dropdown selected-value display dot
    // After the theme updates the selected value display, the swatch span inside
    // may not have data attributes. We find it by class and read the value from
    // the select element or the aria-label.
    var select = list.querySelector('select[name^="option"]');
    if (select) {
      var selectedColorValue = select.value;
      // The selected-value display is a sibling button's inner span
      list.querySelectorAll('.select__color-swatch').forEach(function (dot) {
        // Use data-swatch-value if present, otherwise infer from aria-label or select value
        var dotValue = dot.dataset.swatchValue || dot.getAttribute('aria-label') || selectedColorValue;
        var key = buildComboKey(dotValue, optionPosition, selectedValues, optionCount);
        // For selected-value dots without data-swatch-default-style, compute fallback
        var fallback = dot.dataset.swatchDefaultStyle || ('background-color: ' + dotValue.toLowerCase());
        applySwatchStyle(dot, imageMap[key], fallback);
      });
    }
  }

  /**
   * Update all swatch lists within a given scope element.
   */
  function updateSwatchImages(scope) {
    if (!scope) return;
    var lists = scope.querySelectorAll('[data-swatch-option-position]');
    if (!lists.length) return;

    var selectedValues = getSelectedOptions(scope);
    var optionCount = Object.keys(selectedValues).length;
    if (optionCount === 0) return;

    lists.forEach(function (list) {
      updateSwatchList(list, selectedValues, optionCount);
    });
  }

  function findProductVariants(el) {
    var pv = el.closest('product-variants');
    if (pv) return pv;
    var formAttr = el.getAttribute('form');
    if (formAttr) {
      return document.querySelector('product-variants[form-id="' + formAttr + '"]');
    }
    return null;
  }

  // Hook 1: Direct change events on option inputs
  document.addEventListener('change', function (event) {
    var target = event.target;
    if (!target.matches || !target.matches('[name^="option"]')) return;

    var productVariantsEl = findProductVariants(target);
    if (productVariantsEl) {
      setTimeout(function () {
        updateSwatchImages(productVariantsEl);
      }, 50);
    }
  });

  // Hook 2: variant:changed custom event
  document.addEventListener('variant:changed', function (event) {
    var form = event.target;
    if (!form) return;

    var formId = form.id || form.getAttribute('id');
    var productVariantsEl = formId
      ? document.querySelector('product-variants[form-id="' + formId + '"]')
      : null;

    if (!productVariantsEl && form.closest) {
      productVariantsEl = form.closest('product-variants');
    }

    if (productVariantsEl) {
      updateSwatchImages(productVariantsEl);
    }
  });
})();
